import { Binoculars, GearSix, Hourglass, TrendUp } from '@phosphor-icons/react';
import {
  useListCompetitorPosts,
  useListCompetitors,
} from '@reelplicate/api-client/competitors';
import type { Platform } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  EmptyState,
  FilterChip,
  Inline,
  SegmentedControl,
  SegmentedControlItem,
  Skeleton,
  Stack,
} from '@reelplicate/ui';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';

import { AccountsSection } from './accounts-section';
import { CompetitorPostCard } from './competitor-post-card';
import { platformName } from './format';
import { SuggestionsSection } from './suggestions-section';
import { usePostActions } from './use-post-actions';

const WINDOWS = [
  ['7', 'This week'],
  ['30', 'This month'],
] as const;

const PLATFORMS: Platform[] = ['TIKTOK', 'INSTAGRAM'];

/**
 * What's working for your competitors: posts from their tracked TikTok and
 * Instagram beating their own usual views at least twice over, best first -
 * or, under Taking off, the ones gaining views fastest right now. Save one to
 * the library, or go straight to making your version of it. Their accounts'
 * trends sit above, and accounts like theirs below.
 */
export function CompetitorsPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  // The weekly digest links "Taking off" posts with ?show=taking-off.
  const [params] = useSearchParams();
  const [mode, setMode] = useState<'winners' | 'climbing'>(
    params.get('show') === 'taking-off' ? 'climbing' : 'winners',
  );
  const [days, setDays] = useState('7');
  const [competitorId, setCompetitorId] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [now] = useState(() => Date.now());
  const { data: competitors } = useListCompetitors(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const { data: feed } = useListCompetitorPosts(
    workspaceId,
    {
      ...(mode === 'climbing' ? { sort: 'climbing' as const } : { days: Number(days) }),
      ...(competitorId ? { competitorId } : {}),
      ...(platform ? { platform } : {}),
    },
    { query: { enabled: Boolean(workspaceId) } },
  );
  const actions = usePostActions(workspaceId);

  const tracked = competitors?.items.filter((c) => c.accounts.length > 0) ?? [];

  return (
    <Stack gap={6} className="p-8">
      <header className="flex flex-wrap items-center gap-3">
        <Stack gap={1} className="min-w-64 flex-1">
          <h1 className="text-heading-h1 text-text-primary">Competitors</h1>
          <p className="text-body-md text-text-secondary">
            {mode === 'winners'
              ? 'Their posts beating their own usual views at least twice over.'
              : 'Their newest posts gaining views fastest, against what they usually get.'}
          </p>
        </Stack>
        <SegmentedControl
          value={mode}
          onValueChange={(next) => setMode(next as typeof mode)}
          aria-label="Show"
        >
          <SegmentedControlItem value="winners">Winners</SegmentedControlItem>
          <SegmentedControlItem value="climbing">Taking off</SegmentedControlItem>
        </SegmentedControl>
        {mode === 'winners' && (
          <SegmentedControl value={days} onValueChange={setDays} aria-label="Posted">
            {WINDOWS.map(([value, label]) => (
              <SegmentedControlItem key={value} value={value}>
                {label}
              </SegmentedControlItem>
            ))}
          </SegmentedControl>
        )}
        <Button asChild variant="outline" leadingIcon={GearSix}>
          <RouterLink to="/settings/competitors">Manage</RouterLink>
        </Button>
      </header>

      {workspaceId && tracked.length > 0 && (
        <AccountsSection workspaceId={workspaceId} now={now} />
      )}

      {tracked.length > 0 && (
        <Inline gap={2} wrap role="group" aria-label="Filter posts">
          {tracked.map((competitor) => (
            <FilterChip
              key={competitor.id}
              selected={competitorId === competitor.id}
              onSelectedChange={(on) => setCompetitorId(on ? competitor.id : null)}
            >
              {competitor.label}
            </FilterChip>
          ))}
          {PLATFORMS.map((p) => (
            <FilterChip
              key={p}
              selected={platform === p}
              onSelectedChange={(on) => setPlatform(on ? p : null)}
            >
              {platformName(p)}
            </FilterChip>
          ))}
        </Inline>
      )}

      {!feed ? (
        <Skeleton className="h-96" shape="rectangle" />
      ) : feed.items.length > 0 ? (
        <ul
          aria-label="Competitor posts"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {feed.items.map((post) => (
            <CompetitorPostCard
              key={post.id}
              post={post}
              now={now}
              busy={actions.busyOn(post.id)}
              onSave={() => actions.save(post)}
              onMakeMyVersion={() => actions.remake(post)}
            />
          ))}
        </ul>
      ) : mode === 'climbing' && feed.tracking.accounts > 0 ? (
        <EmptyState
          icon={TrendUp}
          title="Nothing taking off right now"
          description="None of their posts under 3 days old is gaining views unusually fast. Fresh posts are checked every 6 hours."
        />
      ) : (
        <CompetitorsEmpty tracking={feed.tracking} />
      )}

      {workspaceId && <SuggestionsSection workspaceId={workspaceId} />}
    </Stack>
  );
}

/** Why there's nothing to show, and what to do about it. */
export function CompetitorsEmpty({
  tracking,
}: {
  tracking: { accounts: number; syncing: number };
}) {
  if (tracking.accounts === 0) {
    return (
      <EmptyState
        icon={Binoculars}
        title="Track your competitors’ TikTok and Instagram"
        description="Add their handles and we check them daily for the posts beating their usual views."
        actions={
          <Button asChild variant="outline">
            <RouterLink to="/settings/competitors">Add their accounts</RouterLink>
          </Button>
        }
      />
    );
  }
  if (tracking.syncing > 0) {
    return (
      <EmptyState
        icon={Hourglass}
        title="Reading their last 100 posts"
        description="Their winners show up here within the hour."
      />
    );
  }
  return (
    <EmptyState
      icon={Binoculars}
      title="Nothing beating 2× right now"
      description="None of their recent posts is getting twice their usual views. We check again every day."
    />
  );
}
